import type { FeatureKind, LatLon } from './featureGeo';

/** Shape every location source emits (expo-location and the replay provider alike). */
export interface Fix {
  timestamp: number; // ms epoch
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number | null; // metres
    heading: number | null; // degrees, or null / -1 when unknown
    speed: number | null; // m/s, or null / -1 when unknown
  };
}

/** A fix that passed TrackFilter, with heading and speed derived from the track. */
/** Where a clean fix's heading came from; 'held' is the last one kept while stopped. */
export type HeadingSource = 'derived' | 'course' | 'moved' | 'held' | null;

export interface CleanFix extends Fix {
  heading: number | null;
  speedMps: number | null;
  headingSource?: HeadingSource;
}

/** What the app caches locally for each narratable landmark. */
export interface Landmark {
  page_id: number;
  title: string;
  lat: number;
  lon: number;
  trigger_radius_m: number;
  headline: string | null;
  script: string;
  est_seconds: number;
  wiki_url?: string | null;
  /** Wikipedia's coordinate type ('city', 'mountain', 'landmark', ...). A 'city' is a place: it plays once, on arrival. */
  wiki_type?: string | null;
}

/**
 * A rundown about a river, town, county or protected area the car is in or beside, played as
 * filler in a quiet stretch. Keyed by Wikipedia page id, the same key as a landmark, so one
 * played table covers both and a town is never told twice in a trip.
 */
export interface Area {
  page_id: number;
  /** forest and wilderness come from the feature index; town, county and protected from the retired Overpass lookup. */
  kind: 'river' | 'town' | 'county' | 'protected' | 'forest' | 'wilderness' | 'region';
  title: string;
  wiki_url: string | null;
  headline: string | null;
  script: string;
  est_seconds: number;
}

/**
 * A river, national forest or wilderness with a ready script, and its shape round the corridor
 * call that reported it. It plays once the car has run beside the line (or inside the outline)
 * for a while, never on a single point. Keyed by Wikipedia page id like landmarks and areas.
 */
export interface Feature {
  page_id: number;
  kind: FeatureKind;
  title: string;
  wiki_url: string | null;
  headline: string | null;
  script: string;
  est_seconds: number;
  /**
   * The river this one is a branch of when they are told as one (North Santiam of the Santiam):
   * telling the branch covers the parent.
   */
  parent_page_id: number | null;
  /** River: polyline parts. Forest, wilderness: rings, even-odd. [lat, lon], clipped to the call's box. */
  geom: LatLon[][];
}

/** What one corridor call reported: each ready feature's shape clipped round where it was asked. */
export interface FeatureSighting {
  lat: number;
  lon: number;
  features: { page_id: number; geom: LatLon[][] }[];
}

/**
 * Features for a replay. `npm run features` writes whole shapes and no scripts (a placeholder
 * length stands in); `npm run warm` writes the scripts and the sightings the phone would have had.
 */
export interface FeatureFixture {
  features: Feature[];
  sightings?: FeatureSighting[];
}

/** The areas a corridor call reported at one position, most specific first. */
export interface AreaSighting {
  lat: number;
  lon: number;
  page_ids: number[];
}

export interface SpeakOptions {
  voice?: string;
  rate?: number;
  onDone(): void;
  onStopped(): void;
  onError(err: unknown): void;
}

/** Text-to-speech backend. v1: expo-speech. Later: pre-rendered clips with expo-speech fallback. */
export interface Speaker {
  speak(text: string, opts: SpeakOptions): void;
  stop(): void;
  isSpeaking(): boolean | Promise<boolean>;
}

/** Injected time source so the queue runs on virtual time in the replay harness. */
export interface Clock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export const realClock: Clock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};
